// features/item-filter/LocationFilter.jsx
//
// The location dropdown: "All locations" plus the store's locations
// (`useStoreData`). It shows when there is more than one to choose from, and
// when the read failed it shows that failure instead of passing for a store
// without locations.
//
// `activeOnly` offers the active locations only, for a grid whose rows exist
// at active locations only. `valueKey` is the field a picked location hands
// `onChange`: `location_code` (the item-filter endpoints) or `location_id`
// (`/api/reorder-levels/`). "All" is always `'all'`.

import { useEffect, useMemo } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatLocationLabel } from '../../utils/itemLabel';
import { useIsShopify } from '../../contexts/AuthContext';
import { useStoreData } from '../../contexts/StoreDataContext';

const ALL = 'all';

export default function LocationFilter({
  selected,
  onChange,
  activeOnly = false,
  valueKey = 'location_code',
  disabled = false,
}) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const label = t('searchFilters.locationLabel');
  const { locations, error } = useStoreData();
  const failed = error !== null;
  const offered = useMemo(
    () => (activeOnly ? locations.filter((loc) => loc.is_active) : locations),
    [locations, activeOnly],
  );

  // The message says only "couldn't load"; the log carries the cause.
  useEffect(() => {
    if (error) console.error('Locations load failed', error);
  }, [error]);

  if (!failed && offered.length <= 1) return null;

  return (
    <FormControl size="small" className="filter-row-slot" error={failed}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        disabled={disabled || failed}
      >
        <MenuItem value={ALL}>{t('searchFilters.locationAllLabel')}</MenuItem>
        {!failed
          ? offered.map((loc) => (
            <MenuItem key={loc.location_id} value={loc[valueKey]}>
              {formatLocationLabel(loc, isShopify)}
            </MenuItem>
          ))
          // Without the list, a filter still applied is known only by its
          // value: a code is shown where it is the merchant's own; an id, or
          // a code on a Shopify store, leaves the box empty (the helper text
          // below says why).
          : selected !== ALL && (
            <MenuItem value={selected}>
              {valueKey === 'location_code'
                ? formatLocationLabel({ location_code: selected }, isShopify)
                : ''}
            </MenuItem>
          )}
      </Select>
      {failed && <FormHelperText>{t('common.locationsLoadFailed')}</FormHelperText>}
    </FormControl>
  );
}
